import { useQuery } from "@tanstack/react-query";
import type { LiveQueueCurrentResult, LiveQueuesCurrentResponse } from "@situate/contracts/runtime";
import { liveQueuesCurrentResponseSchema } from "@situate/contracts/runtime-zod";
import { useRuntimeHttpClient } from "@/features/runtime-session/hooks/use-runtime-http-client";
import type { LiveViewQueue } from "@/features/deployment-dashboard/live-view/model/queue";

const LIVE_QUEUES_PATH = "/v1/live/queues";
const LIVE_QUEUES_REFETCH_INTERVAL_MS = 10_000;

function mapQueue(result: LiveQueueCurrentResult, observedThrough: string): LiveViewQueue {
  return {
    queue_id: result.queue_id,
    queue_display_name: result.queue_display_name,
    queue_type: result.queue_type,
    area_id: result.area_id,
    peopleInQueue: result.queue_count.value,
    estimatedWaitMinutes: result.estimated_queue_wait_time_minutes.value,
    lastUpdatedAt: observedThrough,
    status: result.rag.state,
    peopleDataStatus: result.queue_count.data_status,
    waitDataStatus: result.estimated_queue_wait_time_minutes.data_status,
    waitQualityReasons: result.estimated_queue_wait_time_minutes.quality_reasons,
  };
}

export function useLiveQueuesCurrent(deploymentId: string) {
  const runtime = useRuntimeHttpClient();
  const client = runtime.client;
  const hasGrant = runtime.operationGrants.some(
    (grant) => grant.operation_id === "live_queues_current"
  );

  const query = useQuery({
    queryKey: ["live-queues-current", deploymentId],
    queryFn: async (): Promise<LiveQueuesCurrentResponse> => {
      if (client === null) throw new Error("Runtime client is unavailable");
      const response = await client.get<unknown>(LIVE_QUEUES_PATH);
      return liveQueuesCurrentResponseSchema.parse(response);
    },
    enabled: client !== null && runtime.deploymentId === deploymentId && hasGrant,
    refetchInterval: LIVE_QUEUES_REFETCH_INTERVAL_MS,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });

  return {
    queues: query.data?.queues.map((queue) => mapQueue(queue, query.data.observed_through)) ?? [],
    isLoading: query.isLoading,
    error: query.error instanceof Error ? query.error : null,
  };
}
